/**
 * VM-14b — Indexmiete-Vorschlag & Anwendung (§ 557b BGB).
 * Extrahiert aus TenancyDetail.jsx.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatEUR, formatDate } from "@/lib/constants";
import { F, Tile } from "./TenancyBits";

export default function IndexRentSection({ tenancy, onApplied }) {
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [adjustedRent, setAdjustedRent] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");

  const loadProposal = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tenancies/${tenancy.id}/index-proposal`);
      setProposal(data);
      setAdjustedRent(String(data.proposed_rent_cold || ""));
      const today = new Date();
      const eff = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      setEffectiveDate(eff.toISOString().slice(0, 10));
    } catch (e) {
      toast.error(formatApiError(e) || "Fehler beim Laden des Vorschlags");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProposal(); /* eslint-disable-next-line */ }, [tenancy.id]);

  const apply = async () => {
    try {
      await api.post(`/tenancies/${tenancy.id}/index-apply`, {
        new_rent_cold: Number(adjustedRent),
        effective_date: effectiveDate,
        new_index_value: proposal?.current_index_value,
        new_index_period: proposal?.current_index_period,
      });
      toast.success("Indexanpassung erfolgreich übernommen");
      setConfirming(false);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anwenden");
    }
  };

  if (loading) return <div className="text-sm text-slate-500 p-4">Lade Vorschlag…</div>;
  if (!proposal) return <div className="text-sm text-slate-500 p-4">Kein Vorschlag verfügbar.</div>;

  return (
    <div className="space-y-4" data-testid="index-rent-section">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-sm uppercase tracking-wider text-slate-500 mb-3 font-semibold">Index-Vergleich</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Tile label="Basis-Index" value={`${proposal.base_index_value} (${proposal.base_index_period})`} />
          <Tile label="Aktueller Index" value={`${proposal.current_index_value} (${proposal.current_index_period})`} />
          <Tile label="Steigerung" value={`${proposal.percent_change > 0 ? "+" : ""}${proposal.percent_change} %`}
            tone={proposal.percent_change >= 0 ? "positive" : "neutral"} />
          <Tile label="Letzte Anpassung" value={proposal.last_increase_date ? formatDate(proposal.last_increase_date) : "—"} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-sm uppercase tracking-wider text-slate-500 mb-3 font-semibold">Mietanpassung</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <Tile label="Aktuelle Kaltmiete" value={formatEUR(proposal.old_rent_cold)} />
          <Tile label="Vorgeschlagene Kaltmiete"
            value={formatEUR(proposal.proposed_rent_cold)}
            tone={proposal.proposed_rent_cold > proposal.old_rent_cold ? "positive" : "neutral"}
            testid="proposed-rent" />
          <Tile label="Differenz" value={formatEUR(proposal.proposed_rent_cold - proposal.old_rent_cold)} />
        </div>

        {!proposal.eligible && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900" data-testid="index-not-eligible">
            <strong>Anpassung derzeit nicht möglich:</strong> {proposal.reason}
          </div>
        )}
        {proposal.threshold_note && (
          <div className="mt-3 bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700">
            <strong>Hinweis:</strong> {proposal.threshold_note}
          </div>
        )}

        {proposal.eligible && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            {!confirming ? (
              <Button onClick={() => setConfirming(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="index-apply-btn">
                Mietanpassung übernehmen
              </Button>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <F label="Neue Kaltmiete €">
                  <Input type="number" step="0.01" value={adjustedRent} onChange={(e) => setAdjustedRent(e.target.value)} data-testid="index-new-rent" />
                </F>
                <F label="Wirksam ab">
                  <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="index-effective-date" />
                </F>
                <div className="flex gap-2">
                  <Button onClick={apply} className="bg-emerald-700 hover:bg-emerald-800" data-testid="index-confirm-btn">Bestätigen</Button>
                  <Button onClick={() => setConfirming(false)} variant="outline">Abbrechen</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="text-[11px] text-slate-500 italic">
        Quelle: Verbraucherpreisindex Deutschland (Basis {proposal.base_index_period.length === 4 ? "2020 = 100" : "Statistisches Bundesamt"}).
        Anpassung nach §557b BGB frühestens 12 Monate nach Vertragsbeginn bzw. letzter Anpassung.
      </div>
    </div>
  );
}
